import { ITheme, onDesktop } from "@teamscheme/theme";
import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  ReactNode,
  HTMLAttributes,
} from "react";
import styled, { css, keyframes } from "styled-components";
import thumbsupIllustration from "./illustrations/thumbsup_120x117.svg";

interface SpeechBubbleProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  isOpen: boolean;
  children: ReactNode;
}

function reviewSectionVariables(theme: ITheme) {
  const white = theme.nafColor.signature.white;
  return {
    white,
  };
}

const SpeechBubbleOpen_left = keyframes`
  0% {
    transform: scale(0.5) translateX(-300px);
  }

  50% {
    transform: scale(0.5);
  }

  100% {
    transform: none;
  }
`;

const SpeechBubbleOpen_right = keyframes`
  0% {
    transform: scale(0.5) translateX(300px);
  }

  50% {
    transform: scale(0.5);
  }

  100% {
    transform: none;
  }
`;

const SpeechBubbleArrow = styled.div`
  display: block;
  border-top: 1em solid transparent;
  border-bottom: 1em solid transparent;
  border-right: 1em solid ${({ theme }) => reviewSectionVariables(theme).white};
  width: 0;
  height: 0;
  position: absolute;
  left: -1em;
  top: 1.5em;
`;
const SpeechBubbleElement = styled.div.attrs(
  (props: { isOpen: boolean } & HTMLAttributes<HTMLDivElement>) => ({
    isOpen: props.isOpen,
  })
)`
  background: ${({ theme }) => reviewSectionVariables(theme).white};
  margin: 1em;
  padding: 2em 3em;
  border-radius: 1em;
  max-width: 30em;
  position: relative;
  margin-bottom: 3em;
  opacity: 0;
  will-change: opacity;

  ${(props) =>
    props.isOpen &&
    css`
      opacity: 1;
      transform: none;
      will-change: none;
      animation: 0.25s ease 0s 1 ${SpeechBubbleOpen_left};
    `}
`;
const SpeechBubbleContent = styled.div``;
const SpeechBubble = styled(
  forwardRef<HTMLDivElement | null, SpeechBubbleProps>(function (
    { isOpen, children, ...props },
    ref
  ) {
    return (
      <div ref={ref} {...props}>
        <SpeechBubbleElement isOpen={isOpen}>
          <SpeechBubbleArrow />
          <SpeechBubbleContent>{children}</SpeechBubbleContent>
        </SpeechBubbleElement>
      </div>
    );
  })
)`
  display: flex;
  flex-direction: column;

  &:nth-child(2n) {
    ${SpeechBubbleElement} {
      align-self: flex-end;
    }

    ${SpeechBubbleArrow} {
      border-right: none;
      border-left: 1em solid
        ${({ theme }) => reviewSectionVariables(theme).white};
      left: auto;
      top: auto;
      right: -1em;
      bottom: 1.5em;
    }
  }

  ${(props) =>
    props.isOpen &&
    css`
      &:nth-child(2n + 1) ${SpeechBubbleElement} {
        animation: 0.25s ease 0s 1 ${SpeechBubbleOpen_left};
      }

      &:nth-child(2n) ${SpeechBubbleElement} {
        animation: 0.25s ease 0s 1 ${SpeechBubbleOpen_right};
      }
    `}
`;

const SPEECH_BUBBLES = [
  <span>
    Gjorde hele bilsalget mitt mye enklere. Gruet meg til å gjennomføre dette,
    men med denne måten å gjøre det på er det nesten så jeg gleder meg til å
    selge en bil igjen. <strong>Olav.</strong>
  </span>,
  <span>
    Veldig enkel og ryddig kjøpekontrakt. Har hatt den på papir og brukt den i
    årevis. Nettversjonen er enda enklere{" "}
    <span role="img" aria-label="Tommel opp">
      👍
    </span>{" "}
    <strong>Halvard.</strong>
  </span>,
  <span>
    Super-enkelt å gjøre dette sammen med en kjøper/selger, og vi får begge
    kontrakten tilsendt direkte når den er godkjent av begge.{" "}
    <strong>Winnie.</strong>
  </span>,
  <span>
    Digital kjøpekontrakt fungerer helt fint, spesielt når kjøper og selger ikke
    er sammen, f.eks. ved at kjøper har sett og prøvekjørt bilen, men tatt
    avgjørelse om kjøp etter å ha kommet hjem. <strong>Geir.</strong>
  </span>,
];

export const ReviewSection = styled(function (
  props: Omit<HTMLAttributes<HTMLDivElement>, "children">
) {
  const [isOpen, setIsOpen] = useState<boolean[]>([]);
  const elements = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (!window.IntersectionObserver) {
      setIsOpen(SPEECH_BUBBLES.map((_) => true));

      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const index = elements.current.findIndex((e) => e === entry.target);
            setIsOpen((x) => [
              ...x.slice(0, index),
              true,
              ...x.slice(index + 1),
            ]);

            observer.unobserve(entry.target);
          }
        }
      },
      {
        threshold: 0.95,
      }
    );

    for (const element of elements.current) {
      if (element) {
        observer.observe(element);
      }
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div {...props}>
      {SPEECH_BUBBLES.map((text, index) => (
        <SpeechBubble
          key={index}
          isOpen={isOpen[index]}
          ref={(x) => (elements.current[index] = x)}
        >
          {text}
        </SpeechBubble>
      ))}
    </div>
  );
})`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
`;

const ReviewHeader = styled.p`
  ${({ theme }) => theme.mobileFontStyle.headers.display}
  text-align: center;
  margin: 0;

  strong {
    color: ${({ theme }) => theme.nafColor.primary.park};
    font-weight: 900;
    font-size: 1.2em;
  }

  ${({ theme }) => onDesktop`
    flex-basis: 70%;
    ${theme.fontStyle.headers.display}
  `}
`;

const ImgWrapper = styled.div`
  display: none;
  flex-basis: 30%;
  margin-right: ${({ theme }) => theme.spacing.space24};
  display: none;

  ${onDesktop`
  display: block;
  img  {
    width: 90%;
    height: auto;
  }
  `}
`;

export const ReviewSectionHeader = styled(function (
  props: Omit<HTMLAttributes<HTMLDivElement>, "children">
) {
  return (
    <div {...props}>
      <ImgWrapper>
        <img src={thumbsupIllustration} alt="Over 100 000 fornøyde brukere" />
      </ImgWrapper>
      <ReviewHeader>
        Over <strong>100&nbsp;000</strong> fornøyde brukere har signert
        digitalt!
      </ReviewHeader>
    </div>
  );
})`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;
